import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useToast } from '../hooks/use-toast';
import { createOrder, getProduct, getVendors, validateCoupon } from '../lib/api';
import Seo from '../components/Seo';
import { getSiteUrl } from '../lib/seo';
import WompiWidgetButton from '../components/WompiWidgetButton';

const WOMPI_PUBLIC_KEY = process.env.REACT_APP_WOMPI_PUBLIC_KEY || '';
const WOMPI_CHECKOUT_URL = process.env.REACT_APP_WOMPI_CHECKOUT_URL || '';
const WOMPI_REDIRECT_BASE =
  process.env.REACT_APP_WOMPI_REDIRECT_URL ||
  (getSiteUrl() ? `${getSiteUrl()}/pago/resultado` : '');
const US_SIZE_EQUIVALENCES = {
  "5.0": "35",
  "6.0": "36",
  "7.0": "40",
  "8.0": "41",
  "9.0": "42",
  "10.0": "43",
  "11.0": "44",
  "12.0": "45",
  "7": "40",
  "8": "41",
  "9": "42",
  "10": "43",
  "11": "44",
  "12": "45",
};
const FREE_SHIPPING_MIN = 200000;
const FLAT_SHIPPING_COST = 10000;

const normalizePhoneNumber = (value) => value.replace(/\D/g, '');

const buildRedirectUrl = (orderCode) => {
  if (!orderCode) return '';
  const base =
    WOMPI_REDIRECT_BASE ||
    `${window.location.origin}/pago/resultado`;
  const separator = base.includes('?') ? '&' : '?';
  return `${base}${separator}order=${encodeURIComponent(orderCode)}`;
};

const normalizeLocalPhone = (value) => {
  if (!value) return '';
  const digits = normalizePhoneNumber(value);
  if (digits.startsWith('57') && digits.length > 10) {
    return digits.slice(2);
  }
  return digits;
};

const Checkout = () => {
  const [carrito, setCarrito] = useState([]);
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    cedula: '',
    email: '',
    telefono: '',
    direccion: '',
    ciudad: '',
    departamento: '',
    codigoPostal: '',
    metodoPago: 'wompi'
  });
  const [submitting, setSubmitting] = useState(false);
  const [wompiOrder, setWompiOrder] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [vendorQuery, setVendorQuery] = useState('');
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [stockChecking, setStockChecking] = useState(false);
  const [stockErrors, setStockErrors] = useState([]);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const carritoGuardado = JSON.parse(localStorage.getItem('carrito') || '[]');
    if (carritoGuardado.length === 0) {
      navigate('/carrito');
      return;
    }
    setCarrito(carritoGuardado);
  }, [navigate]);

  useEffect(() => {
    let isMounted = true;
    setVendorsLoading(true);
    getVendors()
      .then((data) => {
        if (isMounted) {
          setVendors(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (isMounted) setVendorsLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const formatoPrecio = (precio) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(precio);
  };

  const calcularSubtotal = () => {
    return carrito.reduce((total, item) => total + (Number(item.precio) * item.cantidad), 0);
  };

  const subtotal = calcularSubtotal();
  const envio = subtotal >= FREE_SHIPPING_MIN ? 0 : FLAT_SHIPPING_COST;
  const descuento = Number(appliedCoupon?.discount || 0);
  const total = Math.max(0, subtotal - descuento + envio);
  const couponApplied = Boolean(appliedCoupon);
  const couponMeta = appliedCoupon || { percent: 0, amountOff: 0, code: "" };

  useEffect(() => {
    if (!couponApplied) return;
    const normalizedEmail = formData.email.trim().toLowerCase();
    if (
      normalizedEmail !== String(appliedCoupon.email || '').trim().toLowerCase() ||
      subtotal !== Number(appliedCoupon.subtotal || 0)
    ) {
      setAppliedCoupon(null);
    }
  }, [subtotal, formData.email, couponApplied, appliedCoupon]);

  const handleChange = (e) => {
    if (wompiOrder) {
      setWompiOrder(null);
    }
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleVendorInput = (value) => {
    if (wompiOrder) {
      setWompiOrder(null);
    }
    setVendorQuery(value);
    const normalized = value.trim().toLowerCase();
    const match = vendors.find(
      (vendor) => vendor.name.trim().toLowerCase() === normalized
    );
    setSelectedVendorId(match ? match.id : null);
  };

  const vendorSuggestions = React.useMemo(() => {
    const normalized = vendorQuery.trim().toLowerCase();
    if (!normalized) return [];
    return vendors
      .filter((vendor) => vendor.name.toLowerCase().includes(normalized))
      .slice(0, 6);
  }, [vendors, vendorQuery]);

  const handleApplyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) {
      toast({
        title: 'Cupon requerido',
        description: 'Ingresa un codigo de cupon para continuar.',
        variant: 'destructive'
      });
      return;
    }
    if (!formData.email.trim()) {
      toast({
        title: 'Correo requerido',
        description: 'Ingresa tu correo antes de aplicar el cupon.',
        variant: 'destructive'
      });
      return;
    }
    try {
      const result = await validateCoupon({
        code,
        email: formData.email.trim(),
        subtotal
      });
      if (wompiOrder) {
        setWompiOrder(null);
      }
      setCouponCode(result.code || code);
      setAppliedCoupon({
        percent: Number(result.percent || 0),
        amountOff: Number(result.amount_off || 0),
        code: result.code || code,
        discount: Number(result.discount || 0),
        email: formData.email.trim(),
        subtotal: Number(subtotal || 0),
      });
      toast({
        title: 'Cupon aplicado',
        description: `Descuento: ${formatoPrecio(result.discount || 0)}`
      });
    } catch (error) {
      const detail = getErrorMessage(error);
      setAppliedCoupon(null);
      toast({
        title: 'Cupon no valido',
        description: detail,
        variant: 'destructive'
      });
    }
  };

  const getErrorMessage = (error) => {
    if (!error) return 'Error desconocido.';
    if (typeof error === 'string') return error;
    const responseData = error.response?.data;
    let detail = '';
    if (typeof responseData === 'string') {
      detail = responseData;
    } else if (responseData?.detail) {
      detail = responseData.detail;
    } else if (responseData?.message) {
      detail = responseData.message;
    } else if (responseData?.error) {
      detail = responseData.error;
    } else if (error.message) {
      detail = error.message;
    }
    if (!detail) detail = 'Error desconocido.';
    const status = error.response?.status;
    return status ? `Error ${status}: ${detail}` : detail;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) {
      return;
    }
    if (formData.metodoPago === 'wompi' && wompiOrder) {
      toast({
        title: 'Pago listo',
        description: 'Usa el boton de Wompi para completar el pago.'
      });
      return;
    }
    if (formData.metodoPago === 'sistecredito') {
      toast({
        title: 'Sistecredito pronto',
        description: 'Este metodo estara disponible en una proxima actualizacion.'
      });
      return;
    }
    if (!carrito.length) {
      toast({
        title: 'Carrito vacio',
        description: 'Agrega productos antes de continuar.'
      });
      return;
    }

    setSubmitting(true);
    setStockChecking(true);
    try {
      const ids = Array.from(new Set(carrito.map((item) => item.productoId))).filter(Boolean);
      const results = await Promise.allSettled(ids.map((id) => getProduct(id)));
      const stockMap = {};
      const whatsappOnlyMap = {};
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const product = result.value || {};
          stockMap[ids[index]] = product.stock ?? 0;
          whatsappOnlyMap[ids[index]] = Boolean(product.whatsapp_only);
        }
      });
      const requestedByProduct = carrito.reduce((acc, item) => {
        acc[item.productoId] = (acc[item.productoId] || 0) + item.cantidad;
        return acc;
      }, {});
      const insufficient = Object.entries(requestedByProduct)
        .filter(([productId, qty]) => {
          const available = stockMap[productId];
          return available !== undefined && qty > available;
        })
        .map(([productId]) => {
          const item = carrito.find((entry) => entry.productoId === Number(productId));
          const available = stockMap[productId];
          return item
            ? `${item.nombre} solo tiene ${available} unidades disponibles.`
            : `Producto ${productId} tiene stock insuficiente.`;
        });
      const whatsappOnly = Object.entries(requestedByProduct)
        .filter(([productId]) => whatsappOnlyMap[productId])
        .map(([productId]) => {
          const item = carrito.find((entry) => entry.productoId === Number(productId));
          return item
            ? `${item.nombre} solo se vende por WhatsApp.`
            : `Producto ${productId} solo se vende por WhatsApp.`;
        });
      if (insufficient.length) {
        setStockErrors(insufficient);
        toast({
          title: 'Stock insuficiente',
          description: 'Ajusta las cantidades antes de continuar.',
          variant: 'destructive'
        });
        return;
      }
      if (whatsappOnly.length) {
        setStockErrors(whatsappOnly);
        toast({
          title: 'Compra solo por WhatsApp',
          description: 'Estos productos no se pagan con Wompi.',
          variant: 'destructive'
        });
        return;
      }
      setStockErrors([]);
      const payload = {
        customer: {
          name: formData.nombre.trim(),
          last_name: formData.apellido.trim(),
          id_number: formData.cedula.trim(),
          email: formData.email.trim(),
          phone: formData.telefono.trim(),
          address: formData.direccion.trim(),
          city: formData.ciudad.trim(),
          state: formData.departamento.trim(),
          postal_code: formData.codigoPostal.trim() || null
        },
        items: carrito.map((item) => ({
          product_id: item.productoId,
          quantity: item.cantidad,
          size: item.talla || null,
          color: item.color || null,
          image_url: item.imagen || null
        })),
        payment_method: formData.metodoPago,
        coupon_code: couponApplied ? couponCode.trim() : null,
        vendor_id: selectedVendorId ? Number(selectedVendorId) : null
      };

      const order = await createOrder(payload);
      localStorage.setItem('pending_order_code', order.order_code);

      if (formData.metodoPago === 'wompi') {
        if (WOMPI_PUBLIC_KEY) {
          const wompiSignature = order.payment?.signature;
          if (!wompiSignature) {
            throw new Error('No se pudo generar la firma de integridad de Wompi.');
          }
          setWompiOrder(order);
          toast({
            title: 'Pago listo',
            description: 'Haz clic en el boton de Wompi para continuar.'
          });
          return;
        }

        const fallbackUrl = order.payment?.checkout_url || WOMPI_CHECKOUT_URL;
        if (!fallbackUrl) {
          throw new Error('No hay enlace de pago disponible');
        }
        window.location.href = fallbackUrl;
        return;
      }
    } catch (error) {
      const detail = getErrorMessage(error);
      console.error('Error al iniciar pago', error);
      toast({
        title: 'No se pudo iniciar el pago',
        description: detail,
        variant: 'destructive'
      });
    } finally {
      setStockChecking(false);
      setSubmitting(false);
    }
  };

  const parseSizeLabel = (value) => {
    if (!value) return { us: null, eu: null, cm: null };
    const text = String(value);
    const usMatch = text.match(/US\s*([0-9./]+)/i);
    const euMatch = text.match(/EUR\s*([0-9./]+)/i);
    const cmMatch = text.match(/CM\s*([0-9./]+)/i);
    return {
      us: usMatch ? usMatch[1] : null,
      eu: euMatch ? euMatch[1] : null,
      cm: cmMatch ? cmMatch[1] : null
    };
  };

  const getTallaLabel = (item) => {
    if (!item?.talla) return '';
    const parsed = parseSizeLabel(item.talla);
    const usValue = parsed.us || item.talla;
    const tallaEu =
      parsed.eu || item.tallaEu || item.tallaCo || US_SIZE_EQUIVALENCES[item.talla];
    const cmValue = parsed.cm;
    if (!tallaEu && !cmValue) return `Talla US: ${usValue}`;
    if (tallaEu && cmValue) return `Talla US: ${usValue} (EUR ${tallaEu}, CM ${cmValue})`;
    if (tallaEu) return `Talla US: ${usValue} (EUR ${tallaEu})`;
    return `Talla US: ${usValue} (CM ${cmValue})`;
  };

  const isWompi = formData.metodoPago === 'wompi';
  const fullName = `${formData.nombre} ${formData.apellido}`.trim();
  const phoneLocal = normalizeLocalPhone(formData.telefono);
  const wompiCustomerData = {
    email: formData.email.trim() || undefined,
    fullName: fullName || undefined,
    phoneNumber: phoneLocal || undefined,
    phoneNumberPrefix: phoneLocal ? '+57' : undefined,
    legalId: formData.cedula.trim() || undefined,
    legalIdType: formData.cedula.trim() ? 'CC' : undefined
  };
  const wompiShippingAddress = {
    addressLine1: formData.direccion.trim() || undefined,
    addressLine2: undefined,
    country: 'CO',
    city: formData.ciudad.trim() || undefined,
    region: formData.departamento.trim() || undefined,
    phoneNumber: phoneLocal || undefined,
    postalCode: formData.codigoPostal.trim() || undefined,
    name: fullName || undefined
  };
  const wompiButtonOrder =
    isWompi && wompiOrder && WOMPI_PUBLIC_KEY
      ? {
          publicKey: WOMPI_PUBLIC_KEY,
          currency: wompiOrder.currency,
          amountInCents: wompiOrder.amount_in_cents,
          reference: wompiOrder.payment?.wompi_reference || wompiOrder.order_code,
          signature: wompiOrder.payment?.signature,
          redirectUrl: buildRedirectUrl(wompiOrder.order_code)
        }
      : null;
  const submitLabel = submitting
    ? 'Procesando...'
    : isWompi
      ? wompiOrder
        ? 'Pago listo'
        : 'Generar pago'
      : 'Ir a pagar';
  const submitDisabled = submitting || stockChecking || (isWompi && wompiOrder);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <Seo title="Checkout" robots="noindex, nofollow" />
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold mb-8">Finalizar Compra</h1>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Formulario */}
            <div className="lg:col-span-2 space-y-6">
              {/* Información de contacto */}
              <Card>
                <CardHeader>
                  <CardTitle>Información de Contacto</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="nombre">Nombre</Label>
                      <Input
                        id="nombre"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="apellido">Apellido</Label>
                      <Input
                        id="apellido"
                        name="apellido"
                        value={formData.apellido}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="telefono">Teléfono</Label>
                    <Input
                      id="telefono"
                      name="telefono"
                      type="tel"
                      value={formData.telefono}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="cedula">Cedula</Label>
                    <Input
                      id="cedula"
                      name="cedula"
                      value={formData.cedula}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Información de envío */}
              <Card>
                <CardHeader>
                  <CardTitle>Dirección de Envío</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="direccion">Dirección</Label>
                    <Input
                      id="direccion"
                      name="direccion"
                      value={formData.direccion}
                      onChange={handleChange}
                      required
                      placeholder="Calle, número, apartamento"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ciudad">Ciudad</Label>
                      <Input
                        id="ciudad"
                        name="ciudad"
                        value={formData.ciudad}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="departamento">Departamento</Label>
                      <Input
                        id="departamento"
                        name="departamento"
                        value={formData.departamento}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="codigoPostal">Código Postal</Label>
                    <Input
                      id="codigoPostal"
                      name="codigoPostal"
                      value={formData.codigoPostal}
                      onChange={handleChange}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Método de pago */}
              <Card>
                <CardHeader>
                  <CardTitle>Método de Pago</CardTitle>
                </CardHeader>
                <CardContent>
                  <RadioGroup
                    value={formData.metodoPago}
                    onValueChange={(val) => {
                      if (wompiOrder) {
                        setWompiOrder(null);
                      }
                      setFormData({ ...formData, metodoPago: val });
                    }}
                  >
                    <div className="flex items-center space-x-2 mb-3">
                      <RadioGroupItem value="wompi" id="wompi" />
                      <Label htmlFor="wompi" className="cursor-pointer">
                        Wompi (tarjeta y PSE)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 mb-3 opacity-60">
                      <RadioGroupItem value="sistecredito" id="sistecredito" disabled />
                      <Label htmlFor="sistecredito" className="cursor-pointer">
                        Sistecredito (proximamente)
                      </Label>
                    </div>
                  </RadioGroup>
                  <div className="mt-4 p-4 bg-gray-50 rounded">
                    <p className="text-sm text-gray-600">
                      Al continuar se generara el pago y podras abrir el widget de Wompi con el total de tu pedido.
                    </p>
                  </div>
                  {isWompi && wompiButtonOrder && (
                    <div className="mt-4 rounded-lg border border-emerald-100 bg-emerald-50 p-4">
                      <p className="text-sm text-emerald-700 mb-3">
                        Pago listo. Haz clic en el boton de Wompi para completar la compra.
                      </p>
                      <WompiWidgetButton
                        order={wompiButtonOrder}
                        customerData={wompiCustomerData}
                        shippingAddress={wompiShippingAddress}
                        className="flex justify-center"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Resumen del pedido */}
            <div>
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle>Resumen del Pedido</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Items */}
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {carrito.map((item, index) => (
                      <div key={index} className="flex gap-3">
                        <img
                          src={item.imagen}
                          alt={item.nombre}
                          className="w-16 h-16 object-cover rounded"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-semibold line-clamp-1">{item.nombre}</p>
                          <p className="text-xs text-gray-500">x{item.cantidad}</p>
                          {item.talla && (
                            <p className="text-xs text-gray-500">{getTallaLabel(item)}</p>
                          )}
                          <p className="text-sm font-bold">{formatoPrecio(item.precio * item.cantidad)}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t pt-4 space-y-2">
                    <Label htmlFor="coupon">Cupon de descuento</Label>
                    <div className="flex gap-2">
                      <Input
                        id="coupon"
                        value={couponCode}
                        onChange={(event) => {
                          if (wompiOrder) setWompiOrder(null);
                          setCouponCode(event.target.value);
                          if (
                            appliedCoupon &&
                            event.target.value.trim() !== String(appliedCoupon.code || '').trim()
                          ) {
                            setAppliedCoupon(null);
                          }
                        }}
                        placeholder="Codigo de cupon"
                      />
                      <Button type="button" variant="outline" onClick={handleApplyCoupon}>
                        Aplicar
                      </Button>
                    </div>
                    {couponApplied && (
                      <p className="text-xs text-emerald-700">
                        Cupon aplicado correctamente.
                      </p>
                    )}
                  </div>

                  <div className="border-t pt-4 space-y-2">
                    <Label htmlFor="vendor-search">Vendedor</Label>
                    <Input
                      id="vendor-search"
                      value={vendorQuery}
                      onChange={(event) => handleVendorInput(event.target.value)}
                      placeholder="Escribe el nombre del vendedor"
                      autoComplete="off"
                    />
                    {vendorSuggestions.length > 0 && (
                      <div className="rounded border bg-white p-2 text-sm shadow-sm">
                        {vendorSuggestions.map((vendor) => (
                          <button
                            key={vendor.id}
                            type="button"
                            className="block w-full rounded px-2 py-1 text-left hover:bg-gray-100"
                            onClick={() => {
                              handleVendorInput(vendor.name);
                              setSelectedVendorId(vendor.id);
                            }}
                          >
                            {vendor.name}
                          </button>
                        ))}
                      </div>
                    )}
                    {vendorQuery && !selectedVendorId && vendorSuggestions.length === 0 && !vendorsLoading && (
                      <p className="text-xs text-gray-500">No encontramos ese vendedor.</p>
                    )}
                    {vendorsLoading && (
                      <p className="text-xs text-gray-500">Cargando vendedores...</p>
                    )}
                    {!vendorsLoading && vendors.length === 0 && (
                      <p className="text-xs text-gray-500">
                        No hay vendedores registrados.
                      </p>
                    )}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Selecciona quien te recomendo la tienda.</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          handleVendorInput('');
                          setSelectedVendorId(null);
                        }}
                      >
                        Sin vendedor
                      </Button>
                    </div>
                  </div>
                  {stockErrors.length > 0 && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                      {stockErrors.map((message, index) => (
                        <p key={`${message}-${index}`}>{message}</p>
                      ))}
                    </div>
                  )}

                  <div className="border-t pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal:</span>
                      <span>{formatoPrecio(subtotal)}</span>
                    </div>
                    {descuento > 0 && (
                      <div className="flex justify-between text-sm text-emerald-700">
                        <span>
                          {couponMeta.amountOff > 0
                            ? `Cupon ${couponMeta.code || ""}`.trim()
                            : `Cupon ${Math.round((couponMeta.percent || 0) * 10000) / 100}%`}
                          :
                        </span>
                        <span>-{formatoPrecio(descuento)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Envio:</span>
                      <span>{envio === 0 ? 'Gratis' : formatoPrecio(envio)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold pt-2 border-t">
                      <span>Total:</span>
                      <span>{formatoPrecio(total)}</span>
                    </div>
                  </div>

                  <Button type="submit" className="w-full" size="lg" disabled={submitDisabled}>
                    {submitLabel}
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    Al realizar el pedido aceptas nuestros términos y condiciones
                  </p>
                  <p className="text-xs text-gray-500 text-center">
                    Envio gratis desde $200.000. Envio $10.000 en compras menores. Recibe un cupon del 5% en compras superiores a $200.000.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
